import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  Switch,
  Chip,
  IconButton,
  Button,
  Tooltip,
  Grid,
  useTheme,
  alpha,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";

import type { CustomQuestion } from "./types";
import { QTYPES, CAT_LABELS, OPERATORS } from "./constants";

const QRow: React.FC<{
  q: CustomQuestion;
  idx: number;
  allQs: CustomQuestion[];
  onUpdate: (q: CustomQuestion) => void;
  onDelete: () => void;
  onDragStart: (e: React.DragEvent, idx: number) => void;
  onDragOver: (e: React.DragEvent, idx: number) => void;
  onDragEnd: () => void;
  isDragOver: boolean;
}> = ({
  q,
  idx,
  allQs,
  onUpdate,
  onDelete,
  onDragStart,
  onDragOver,
  onDragEnd,
  isDragOver,
}) => {
  const theme = useTheme();
  const [exp, setExp] = useState(false);
  const otherQs = allQs.filter((oq) => oq.id !== q.id);
  const parentQ = q.condition
    ? allQs.find((oq) => oq.id === q.condition!.questionId)
    : null;

  return (
    <Box
      draggable
      onDragStart={(e) => onDragStart(e, idx)}
      onDragOver={(e) => onDragOver(e, idx)}
      onDragEnd={onDragEnd}
      sx={{
        borderRadius: "10px",
        border: `1.5px solid ${isDragOver ? theme.palette.primary.main : q.condition ? alpha("#A78BFA", 0.3) : theme.palette.divider}`,
        overflow: "hidden",
        bgcolor: theme.palette.background.paper,
        boxShadow: exp
          ? `0 2px 8px ${alpha(theme.palette.common.black, 0.06)}`
          : "none",
        transition: "all 0.2s ease",
        ...(isDragOver && {
          transform: "scale(1.01)",
          boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.15)}`,
        }),
      }}
    >
      {/* Collapsed header */}
      <Box
        sx={{
          p: "8px 12px",
          display: "flex",
          alignItems: "center",
          gap: 0.75,
        }}
      >
        <Box
          sx={{
            cursor: "grab",
            display: "flex",
            alignItems: "center",
            p: 0.25,
            borderRadius: "4px",
            mr: 0.25,
            "&:hover": { bgcolor: theme.palette.action.hover },
            "&:active": { cursor: "grabbing" },
          }}
        >
          <DragIndicatorIcon sx={{ fontSize: 16, color: "text.disabled" }} />
        </Box>
        <Box
          sx={{
            width: 26,
            height: 26,
            borderRadius: "6px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 11,
            fontWeight: 700,
            bgcolor: q.condition
              ? alpha("#A78BFA", 0.1)
              : alpha(theme.palette.primary.main, 0.08),
            color: q.condition ? "#A78BFA" : "primary.main",
          }}
        >
          {q.condition ? (
            <BoltOutlinedIcon sx={{ fontSize: 14 }} />
          ) : (
            QTYPES.find((t) => t.id === q.type)?.icon || "?"
          )}
        </Box>
        <Box
          sx={{ flex: 1, minWidth: 0, cursor: "pointer" }}
          onClick={() => setExp(!exp)}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography
              variant="body2"
              fontWeight={600}
              noWrap
              sx={{ fontSize: 12 }}
            >
              {q.label || "Untitled"}
            </Typography>
            {q.required && (
              <Chip
                label="Required"
                size="small"
                sx={{
                  height: 16,
                  fontSize: 8,
                  fontWeight: 700,
                  bgcolor: alpha("#FF4D6A", 0.1),
                  color: "#FF4D6A",
                  "& .MuiChip-label": { px: 0.5 },
                }}
              />
            )}
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: 10 }}
          >
            {q.condition ? (
              <>
                Conditional · Shows when &quot;{parentQ?.label || "..."}&quot;{" "}
                {q.condition.operator} &quot;{q.condition.value || "..."}&quot;
              </>
            ) : (
              <>
                {QTYPES.find((t) => t.id === q.type)?.label} ·{" "}
                {CAT_LABELS[q.category]}
              </>
            )}
          </Typography>
        </Box>
        {/* Expand/Collapse */}
        <Tooltip title={exp ? "Collapse" : "Edit"} arrow>
          <IconButton
            size="small"
            onClick={() => setExp(!exp)}
            sx={{
              width: 26,
              height: 26,
              borderRadius: "6px",
              color: exp ? "primary.main" : "text.secondary",
              bgcolor: exp
                ? alpha(theme.palette.primary.main, 0.08)
                : "transparent",
              "&:hover": { bgcolor: theme.palette.action.hover },
            }}
          >
            {exp ? (
              <ExpandLessIcon sx={{ fontSize: 16 }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: 16 }} />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Expanded editor */}
      {exp && (
        <Box
          sx={{
            p: "12px 14px",
            borderTop: `1px solid ${theme.palette.divider}`,
            bgcolor: alpha(theme.palette.background.default, 0.5),
          }}
        >
          <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
            <Grid item xs={6}>
              <Typography
                variant="caption"
                fontWeight={600}
                color="text.secondary"
                sx={{ fontSize: 10, mb: 0.5, display: "block" }}
              >
                Label
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={q.label}
                onChange={(e) => onUpdate({ ...q, label: e.target.value })}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
              />
            </Grid>
            <Grid item xs={6}>
              <Typography
                variant="caption"
                fontWeight={600}
                color="text.secondary"
                sx={{ fontSize: 10, mb: 0.5, display: "block" }}
              >
                Type
              </Typography>
              <Select
                fullWidth
                size="small"
                value={q.type}
                onChange={(e) =>
                  onUpdate({
                    ...q,
                    type: e.target.value as CustomQuestion["type"],
                  })
                }
                sx={{ borderRadius: "8px" }}
              >
                {QTYPES.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.icon} {t.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          </Grid>
          <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
            <Grid item xs={6}>
              <Typography
                variant="caption"
                fontWeight={600}
                color="text.secondary"
                sx={{ fontSize: 10, mb: 0.5, display: "block" }}
              >
                Category
              </Typography>
              <Select
                fullWidth
                size="small"
                value={q.category}
                onChange={(e) => onUpdate({ ...q, category: e.target.value })}
                sx={{ borderRadius: "8px" }}
              >
                {Object.entries(CAT_LABELS).map(([k, v]) => (
                  <MenuItem key={k} value={k}>
                    {v}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={6}>
              <Typography
                variant="caption"
                fontWeight={600}
                color="text.secondary"
                sx={{ fontSize: 10, mb: 0.5, display: "block" }}
              >
                Required
              </Typography>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.75 }}
              >
                <Switch
                  checked={q.required}
                  onChange={() => onUpdate({ ...q, required: !q.required })}
                  size="small"
                />
                <Typography
                  variant="body2"
                  fontWeight={500}
                  color={q.required ? "primary" : "text.secondary"}
                  sx={{ fontSize: 12 }}
                >
                  {q.required ? "Yes" : "No"}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {(q.type === "dropdown" || q.type === "checkbox") && (
            <Box sx={{ mb: 1.5 }}>
              <Typography
                variant="caption"
                fontWeight={600}
                color="text.secondary"
                sx={{ fontSize: 10, mb: 0.5, display: "block" }}
              >
                Options (one per line)
              </Typography>
              <TextField
                fullWidth
                size="small"
                multiline
                rows={3}
                value={(q.options || []).join("\n")}
                onChange={(e) =>
                  onUpdate({ ...q, options: e.target.value.split("\n") })
                }
                sx={{
                  "& .MuiInputBase-input": {
                    fontFamily: "monospace",
                    fontSize: 11.5,
                  },
                  "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                }}
              />
            </Box>
          )}

          {/* Conditional Logic */}
          <Box
            sx={{
              p: 1.5,
              borderRadius: "8px",
              bgcolor: q.condition
                ? alpha("#A78BFA", 0.06)
                : theme.palette.background.paper,
              border: `1px solid ${q.condition ? alpha("#A78BFA", 0.25) : theme.palette.divider}`,
              mb: 1.5,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: q.condition ? 1.25 : 0,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: "6px",
                    bgcolor: q.condition
                      ? alpha("#A78BFA", 0.15)
                      : theme.palette.action.hover,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <BoltOutlinedIcon
                    sx={{
                      fontSize: 13,
                      color: q.condition ? "#A78BFA" : "text.disabled",
                    }}
                  />
                </Box>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color={q.condition ? "#A78BFA" : "text.secondary"}
                  sx={{
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    fontSize: 10,
                  }}
                >
                  Conditional Logic
                </Typography>
              </Box>
              <Switch
                checked={!!q.condition}
                size="small"
                onChange={() =>
                  onUpdate({
                    ...q,
                    condition: q.condition
                      ? null
                      : {
                          questionId: otherQs[0]?.id || "",
                          operator: "equals",
                          value: "",
                        },
                  })
                }
              />
            </Box>
            {q.condition && (
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 1, display: "block", fontSize: 10 }}
                >
                  Show this question only when:
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    gap: 0.75,
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  <Select
                    size="small"
                    value={q.condition.questionId}
                    onChange={(e) =>
                      onUpdate({
                        ...q,
                        condition: {
                          ...q.condition!,
                          questionId: e.target.value,
                        },
                      })
                    }
                    sx={{
                      flex: 1,
                      minWidth: 80,
                      borderRadius: "8px",
                      fontSize: 11,
                    }}
                  >
                    <MenuItem value="">Select question...</MenuItem>
                    {otherQs.map((oq) => (
                      <MenuItem key={oq.id} value={oq.id}>
                        {oq.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <Select
                    size="small"
                    value={q.condition.operator}
                    onChange={(e) =>
                      onUpdate({
                        ...q,
                        condition: {
                          ...q.condition!,
                          operator: e.target.value,
                        },
                      })
                    }
                    sx={{ width: 130, borderRadius: "8px", fontSize: 11 }}
                  >
                    {OPERATORS.map((op) => (
                      <MenuItem key={op.id} value={op.id}>
                        {op.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {parentQ &&
                  (parentQ.type === "dropdown" ||
                    parentQ.type === "checkbox") ? (
                    <Select
                      size="small"
                      value={q.condition.value}
                      onChange={(e) =>
                        onUpdate({
                          ...q,
                          condition: { ...q.condition!, value: e.target.value },
                        })
                      }
                      sx={{
                        flex: 1,
                        minWidth: 80,
                        borderRadius: "8px",
                        fontSize: 11,
                      }}
                    >
                      <MenuItem value="">Select value...</MenuItem>
                      {(parentQ.options || []).map((o) => (
                        <MenuItem key={o} value={o}>
                          {o}
                        </MenuItem>
                      ))}
                    </Select>
                  ) : (
                    <TextField
                      size="small"
                      value={q.condition.value}
                      onChange={(e) =>
                        onUpdate({
                          ...q,
                          condition: { ...q.condition!, value: e.target.value },
                        })
                      }
                      placeholder="Value..."
                      sx={{
                        flex: 1,
                        minWidth: 80,
                        "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                      }}
                    />
                  )}
                </Box>
                <Box
                  sx={{
                    mt: 1,
                    p: "6px 10px",
                    borderRadius: "6px",
                    bgcolor: alpha("#A78BFA", 0.08),
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: "#A78BFA", fontStyle: "italic", fontSize: 10 }}
                  >
                    {"\u2192"} If &quot;{parentQ?.label || "..."}&quot;{" "}
                    {q.condition.operator} &quot;{q.condition.value || "..."}
                    &quot;, then show &quot;{q.label}&quot;
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              size="small"
              color="error"
              startIcon={<DeleteOutlineIcon sx={{ fontSize: 15 }} />}
              onClick={onDelete}
              sx={{
                textTransform: "none",
                fontSize: 11,
                fontWeight: 600,
                borderRadius: "7px",
              }}
            >
              Delete
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default QRow;
